import { useCallback, useEffect, useMemo, useState } from 'react'
import { Flex } from '@dd3tech/wiggui/Flex'
import { Checkbox } from '@dd3tech/wiggui/Checkbox'
import { Spinner } from '@dd3tech/wiggui/Spinnner'
import { Text } from '@dd3tech/wiggui/Text'
import { Badge } from '@dd3tech/wiggui/Badge'
import { useModalAddList } from '../hooks/useModalAddList'
import { useModalAddListStore } from '../../../store'
import { composeClasses } from '@wiggot/utils'
import type { IList } from '../../../../interfaces'
import { useIntersectionObserver } from '../../../hooks/ds/useIntersectionObserver'

export const List = ({
  list,
  listIndex
}: {
  list: IList
  listIndex: number
}) => {
  const { isVisible, observedElementRef, disconnect } =
    useIntersectionObserver<HTMLButtonElement>({
      threshold: 0
    })

  const { handleAddOrDeletePropertyInList, getLists } = useModalAddList()

  const dataPagination = useModalAddListStore((state) => state?.pagination)
  const dataPaginationIsLastPage = useMemo(
    () => dataPagination?.page >= dataPagination?.pages,
    [dataPagination]
  )

  const isLoadingGetLists = useModalAddListStore(
    (state) => state?.isLoadingGetLists
  )
  const isLoadingAddOrDeletePropertyInList = useModalAddListStore(
    (state) => state?.isLoadingAddOrDeletePropertyInList
  )
  const isLoadingCurrentList =
    isLoadingAddOrDeletePropertyInList?.isLoading &&
    isLoadingAddOrDeletePropertyInList?.listId === list?.id

  const dataLists = useModalAddListStore((state) => state?.lists)
  const isLastCard = dataLists && listIndex === dataLists?.length - 1

  const [isChecked, setIsChecked] = useState<boolean>(list?.propertyExists)

  const onChange = (value: boolean) => {
    handleAddOrDeletePropertyInList({
      type: value ? 'DELETE' : 'ADD',
      listId: list?.id,
      onSuccess: () => setIsChecked(!isChecked)
    })
  }

  const handleRefetchLists = useCallback(() => {
    if (dataPaginationIsLastPage) {
      disconnect()
      return
    }

    getLists({ page: dataPagination?.page + 1 })
  }, [dataPaginationIsLastPage, disconnect, getLists, dataPagination])

  useEffect(() => {
    if (isVisible && isLastCard && !isLoadingGetLists) {
      handleRefetchLists()
    }
  }, [isVisible])

  return (
    <button
      type="button"
      ref={observedElementRef}
      className={composeClasses(
        isLoadingCurrentList && 'pointer-events-none opacity-70',
        'w-full gap-2 px-4 py-1.5 flex items-center border-b border-surface-strokes justify-between flex-row hover:bg-surface-gray-10 rounded-md'
      )}
      onClick={() => onChange(isChecked)}
    >
      <Flex wFull gap="gap-3" items="center" justify="start" dir="row">
        <Checkbox
          onChange={() => ({})}
          isDisabled={isLoadingCurrentList}
          type="checkbox"
          isChecked={isChecked}
        />
        <Flex wFull items="start" justify="center" dir="col">
          <Badge
            size="small"
            variant="light"
            type="blue"
            value={` ${list?.listPropertiesCount} ${
              list?.listPropertiesCount === 1 ? 'propiedad' : 'propiedades'
            }`}
          />
          <Text className="text-body-l text-primary capitalize">
            {list?.name}
          </Text>
        </Flex>
      </Flex>
      {isLoadingCurrentList && <Spinner customBorderColor="border-t-[#000]" />}
    </button>
  )
}
